import { create } from "zustand";
import { persist } from "zustand/middleware";
import { CartItem, Cart, AppliedPromo } from "@/types/cart.types";

interface CartState {
    items: CartItem[];
    isOpen: boolean;

    openCart: () => void;
    closeCart: () => void;

    addItem: (item: CartItem) => void;
    removeItem: (id: string) => void;
    updateQuantity: (id: string, qty: number) => void;
    clearCart: () => void;
    
    sessionId: string | null;
    setSessionId: (id: string | null) => void;
    setCart: (cart: Cart) => void;

    promo: AppliedPromo | null;
    setPromo: (promo: AppliedPromo | null) => void;
}

export const useCartStore = create<CartState>()(
    persist(
        (set) => ({
            items: [],
            isOpen: false,
            sessionId: null,
            promo: null,

            openCart: () => set({ isOpen: true }),
            closeCart: () => set({ isOpen: false }),
            setSessionId: (id) => set({ sessionId: id }),
            setPromo: (promo: AppliedPromo | null) => set({ promo }),
            setCart: (cart: Cart) => {
                set({ sessionId: cart.sessionId || null });
                const newItems: CartItem[] = cart.items.map((item) => ({
                    id: item.id,
                    productId: item.productId,
                    attributeOptionId: item.attributeOptionId,
                    name: item.name,
                    image: item.image,
                    price: item.price,
                    label: item.label,
                    quantity: item.quantity,
                }));
                set({ items: newItems });
            },

            addItem: (item) =>
                set((state) => {
                    const existing = state.items.find((i) => i.id === item.id);

                    if (existing) {
                        return {
                            items: state.items.map((i) =>
                                i.id === item.id
                                    ? { ...i, quantity: i.quantity + item.quantity }
                                    : i
                            ),
                        };
                    }

                    return {
                        items: [...state.items, item],
                    };
                }),

            removeItem: (id) =>
                set((state) => ({
                    items: state.items.filter((i) => i.id !== id),
                })),

            updateQuantity: (id, qty) =>
                set((state) => ({
                    items: state.items.map((i) =>
                        i.id === id ? { ...i, quantity: Math.max(1, qty) } : i
                    ),
                })),

            clearCart: () => set({ items: [] }),
        }),
        { 
            name: "cart-storage",
            partialize: (state) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { promo, ...rest } = state;
                return rest;
            }
        }
    )
);
